import React, { useCallback, useState } from "react"
import { Segmented } from "antd"

import BaseCheckReport from "features/DataChecks/components/BaseCheckReport.component"
import CleanPanel from "features/DataChecks/components/CleanPanel.component"
import { useBaseCheck } from "hooks/api/useBaseCheck"
import { useProjectStore } from "stores/projectStore"

/**
 * The sections of the Data Checks tab. Local to this feature — nothing deep-links into a
 * section yet, so (unlike Provenance) it is not part of the project nav store.
 */
type DataChecksSection = "base" | "cleaning"

/**
 * Data Checks tab — the project-level home for "is this data what the routing says it should
 * be?". Sections:
 *  - Base check — compares who the questionnaire's routing says should have been ASKED each
 *                 question against who actually answered it, so unexpected bases (routing bugs,
 *                 bad imports, mis-mapped fields) surface before analysis starts.
 *  - Cleaning   — applies that report: blanks the answers the routing says were never asked,
 *                 after an explicit confirmation, keeping a pre-clean copy of the data.
 *
 * The tab needs a questionnaire to derive routing from, so it is hidden for data-only projects
 * (see the visibility filter in ProjectDetailsTabs.container).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * ONE REPORT, TWO SECTIONS — and how it is shared
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * A clean is an approval of ONE report: the flow it walked chooses what the server walks, and
 * its `dataHash` is the consent the server re-checks before it writes. The Cleaning section
 * therefore cannot have a report of its own — a second run would be a second report, and the
 * user would be applying numbers they never read.
 *
 * The sharing is done through the react-query CACHE rather than by prop-drilling: this
 * container subscribes to `useBaseCheck` with the same arguments the Base check section uses,
 * so both observers read and write ONE entry under
 * `queryKeys.dataChecks.baseCheck(projectId, variant)`. Running the check from either place
 * updates both, and `refetch()` here is the same refetch there.
 *
 * Why not lift the state and pass it down as props, which is the more usual answer? Because the
 * cache is already the single source of truth, one key with N observers is exactly what
 * react-query is for, and the alternative would mean rewriting the Base check section's props —
 * a file this task does not own — to gain nothing the cache does not already give. The one cost
 * is stated rather than hidden: this container holds an observer for as long as the tab is
 * mounted, so the entry's 5-minute `gcTime` cannot start running out while the user is on the
 * tab. That is the behaviour we want anyway.
 *
 * The query is `enabled: false`, so this extra observer never causes a fetch — the check runs
 * only when someone presses the button.
 */
const DataChecksContainer: React.FC = () => {
  const [section, setSection] = useState<DataChecksSection>("base")
  const project = useProjectStore((s) => s.project)

  // The SAME query entry the Base check section renders. Owner hint only — the server resolves
  // it for your own projects.
  const {
    data: report,
    isFetching,
    refetch,
  } = useBaseCheck(project.id, project.Created_by)

  const refetchReport = useCallback((): void => {
    void refetch()
  }, [refetch])

  const goToBaseCheck = useCallback((): void => setSection("base"), [])

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-2xl bg-white">
      <div className="border-b border-slate-200 px-4 pt-3 pb-2">
        <Segmented
          value={section}
          onChange={(v) => setSection(v as DataChecksSection)}
          options={[
            { label: "Base check", value: "base" },
            { label: "Cleaning", value: "cleaning" },
          ]}
        />
      </div>

      <div className="min-h-0 flex-1">
        {section === "base" ? (
          <div className="h-full overflow-y-auto">
            <BaseCheckReport projectId={project.id} owner={project.Created_by} />
          </div>
        ) : (
          <div className="h-full overflow-y-auto">
            <CleanPanel
              projectId={project.id}
              owner={project.Created_by}
              report={report}
              isReportFetching={isFetching}
              refetchReport={refetchReport}
              onGoToBaseCheck={goToBaseCheck}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default DataChecksContainer
